import { useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm";
import { PendingInline } from "@/components/ui/pending-indicator";
import { Textarea } from "@/components/ui/textarea";
import { fetchMe } from "@/shared/api/auth";
import {
  createComment,
  deleteComment,
  listComments,
  listCompanyMembers,
  toggleCommentReaction,
  updateComment,
  type CommentDocType,
  type CommentRow,
  type CompanyMember,
} from "@/shared/api/comments";
import { getProduct, listProducts } from "@/shared/api/inventory";
import { getPurchaseOrder, listPurchaseOrders } from "@/shared/api/purchasing";
import { getSalesOrder, listSalesOrders } from "@/shared/api/sales";
import { PersonAvatar } from "@/modules/shell/PersonAvatar";
import { relativeTimeLabel } from "@/modules/shell/relative-time";
import { DocMentionPreview } from "./DocMentionPreview";
import {
  activeMentionQuery,
  filterDocsForMention,
  filterMembersForMention,
  insertDocMentionAt,
  insertMentionAt,
  parseCommentBodySegments,
  parseDocMentionRefs,
  type DocMentionCandidate,
  type DocMentionKind,
} from "../mention-math";

const REACTIONS = [
  { code: "ack" as const, label: "👍" },
  { code: "seen" as const, label: "👀" },
  { code: "done" as const, label: "✅" },
];

function CommentBody(props: {
  body: string;
  members: CompanyMember[];
  docs: DocMentionCandidate[];
  onDocClick?: (kind: DocMentionKind, id: string) => void;
}) {
  const segments = parseCommentBodySegments(
    props.body,
    props.members.map((m) => ({ id: m.id, label: m.label })),
    props.docs,
  );
  return (
    <p className="m-0 whitespace-pre-wrap text-[0.9375rem] leading-relaxed text-[var(--color-foreground)]">
      {segments.map((seg, i) => {
        if (seg.kind === "text") {
          return <span key={i}>{seg.value}</span>;
        }
        if (seg.kind === "docMention") {
          return (
            <button
              key={i}
              type="button"
              className="mx-0.5 inline-flex items-center rounded-full border border-[var(--color-border)] bg-[var(--color-background)] px-2 py-0.5 text-[0.8125rem] font-medium text-[var(--color-foreground)] hover:border-[var(--color-muted-fg)]"
              onClick={() => props.onDocClick?.(seg.docKind, seg.docId)}
            >
              @{seg.label}
            </button>
          );
        }
        return (
          <span
            key={i}
            className="mx-0.5 inline-flex items-center rounded-full bg-[color-mix(in_oklab,var(--color-primary)_14%,transparent)] px-2 py-0.5 text-[0.8125rem] font-medium text-[var(--color-primary)]"
          >
            @{seg.label}
          </span>
        );
      })}
    </p>
  );
}

/** Facebook-familiar thread + composer — plan/22, plan/35 */
export function CommentsPanel(props: {
  companySlug: string;
  docType: CommentDocType;
  docId: string;
  linkPath?: string;
  /** Hide outer chrome when nested in DocActivity */
  embedded?: boolean;
}) {
  const { companySlug, docType, docId, linkPath, embedded } = props;
  const confirm = useConfirm();
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [meId, setMeId] = useState<string | null>(null);
  const [meLabel, setMeLabel] = useState("You");
  const [body, setBody] = useState("");
  const [cursor, setCursor] = useState(0);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [docCatalog, setDocCatalog] = useState<DocMentionCandidate[]>([]);
  const [docSearchBusy, setDocSearchBusy] = useState(false);
  const [preview, setPreview] = useState<{
    kind: DocMentionKind;
    id: string;
  } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  async function reload() {
    const [c, m] = await Promise.all([
      listComments(companySlug, docType, docId),
      listCompanyMembers(companySlug),
    ]);
    setComments(c.comments);
    setMembers(m.members);
  }

  useEffect(() => {
    let cancelled = false;
    Promise.all([reload(), fetchMe()])
      .then(([, me]) => {
        if (cancelled) return;
        setMeId(me.user.id);
        setMeLabel(
          me.user.displayName?.trim() ||
            me.user.email ||
            "You",
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed");
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySlug, docType, docId]);

  const mention = activeMentionQuery(body, cursor);
  const peopleSuggestions = mention
    ? filterMembersForMention(
        members.map((m) => ({ id: m.id, label: m.label })),
        mention.query,
        6,
      )
    : [];
  const docSuggestions = mention
    ? filterDocsForMention(docCatalog, mention.query, 6)
    : [];

  useEffect(() => {
    if (!mention) return;
    const q = mention.query.trim();
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setDocSearchBusy(true);
      void (async () => {
        try {
          const [so, po, products] = await Promise.all([
            listSalesOrders(companySlug, { q: q || undefined, limit: 8 }),
            listPurchaseOrders(companySlug, { q: q || undefined, limit: 8 }),
            listProducts(companySlug, { q: q || undefined, limit: 8 }),
          ]);
          if (cancelled) return;
          const docs: DocMentionCandidate[] = [
            ...so.orders.map((o) => ({
              kind: "sales_order" as const,
              id: o.id,
              label: o.number,
              hint: o.customerName,
            })),
            ...po.orders.map((o) => ({
              kind: "purchase_order" as const,
              id: o.id,
              label: o.number,
              hint: o.supplierName,
            })),
            ...products.products.map((p) => ({
              kind: "product" as const,
              id: p.id,
              label: p.name,
              hint: p.sku ?? undefined,
            })),
          ];
          setDocCatalog((prev) => {
            const map = new Map(
              prev.map((d) => [`${d.kind}:${d.id}`, d] as const),
            );
            for (const d of docs) map.set(`${d.kind}:${d.id}`, d);
            return [...map.values()];
          });
        } catch {
          /* typeahead is best-effort */
        } finally {
          if (!cancelled) setDocSearchBusy(false);
        }
      })();
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [mention?.query, mention?.start, companySlug]);

  // Resolve labels for @doc tokens already in the thread.
  useEffect(() => {
    const refs = comments.flatMap((c) => parseDocMentionRefs(c.body));
    if (refs.length === 0) return;
    let cancelled = false;
    void (async () => {
      const extras: DocMentionCandidate[] = [];
      for (const r of refs.slice(0, 16)) {
        try {
          if (r.kind === "sales_order") {
            const { order } = await getSalesOrder(companySlug, r.id);
            extras.push({
              kind: "sales_order",
              id: r.id,
              label: order.number,
              hint: order.customerName,
            });
          } else if (r.kind === "purchase_order") {
            const { order } = await getPurchaseOrder(companySlug, r.id);
            extras.push({
              kind: "purchase_order",
              id: r.id,
              label: order.number,
              hint: order.supplierName,
            });
          } else {
            const { product } = await getProduct(companySlug, r.id);
            extras.push({
              kind: "product",
              id: r.id,
              label: product.name,
              hint: product.sku ?? undefined,
            });
          }
        } catch {
          extras.push({
            kind: r.kind,
            id: r.id,
            label: `${r.kind} ${r.id.slice(0, 8)}`,
          });
        }
      }
      if (cancelled || extras.length === 0) return;
      setDocCatalog((prev) => {
        const map = new Map(prev.map((d) => [`${d.kind}:${d.id}`, d] as const));
        for (const d of extras) map.set(`${d.kind}:${d.id}`, d);
        return [...map.values()];
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [comments, companySlug]);

  function focusCursor(nextCursor: number) {
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(nextCursor, nextCursor);
    });
  }

  function pickMention(userId: string) {
    if (!mention) return;
    const next = insertMentionAt(body, cursor, mention.start, userId);
    setBody(next.body);
    setCursor(next.cursor);
    setMentionIds((ids) =>
      ids.includes(next.mentionUserId) ? ids : [...ids, next.mentionUserId],
    );
    focusCursor(next.cursor);
  }

  function pickDocMention(kind: DocMentionKind, id: string) {
    if (!mention) return;
    const next = insertDocMentionAt(body, cursor, mention.start, kind, id);
    setBody(next.body);
    setCursor(next.cursor);
    focusCursor(next.cursor);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createComment(companySlug, {
        docType,
        docId,
        body: body.trim(),
        mentionUserIds: mentionIds.length ? mentionIds : undefined,
        linkPath,
      });
      setBody("");
      setCursor(0);
      setMentionIds([]);
      setComposerOpen(false);
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  const thread = (
    <>
      <ul className="m-0 list-none space-y-5 p-0">
        {comments.map((c) => {
          const mine = meId != null && c.authorUserId === meId;
          const editing = editingId === c.id;
          return (
            <li key={c.id} className="flex gap-3">
              <PersonAvatar
                label={c.authorLabel}
                size="lg"
                variant="solid"
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-sm font-semibold">
                    {c.authorLabel}
                  </span>
                  <span
                    className="text-xs text-[var(--color-muted)]"
                    title={new Date(c.createdAt).toLocaleString("en-PH", {
                      timeZone: "Asia/Manila",
                    })}
                  >
                    {relativeTimeLabel(c.createdAt)}
                    {c.editedAt ? " · edited" : ""}
                  </span>
                </div>
                {editing ? (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      className="min-h-16 rounded-xl"
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        disabled={busy || !editBody.trim()}
                        onClick={() => {
                          setBusy(true);
                          setError(null);
                          void updateComment(
                            companySlug,
                            c.id,
                            editBody.trim(),
                          )
                            .then(() => {
                              setEditingId(null);
                              setEditBody("");
                              return reload();
                            })
                            .catch((err: unknown) =>
                              setError(
                                err instanceof Error ? err.message : "Failed",
                              ),
                            )
                            .finally(() => setBusy(false));
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(null);
                          setEditBody("");
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1">
                    <CommentBody
                      body={c.body}
                      members={members}
                      docs={docCatalog}
                      onDocClick={(kind, id) => setPreview({ kind, id })}
                    />
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1">
                  {REACTIONS.map((r) => {
                    const count =
                      c.reactions.find((x) => x.reaction === r.code)
                        ?.count ?? 0;
                    const myReact = c.myReactions.includes(r.code);
                    return (
                      <button
                        key={r.code}
                        type="button"
                        disabled={busy}
                        title={r.code}
                        onClick={() => {
                          setBusy(true);
                          void toggleCommentReaction(
                            companySlug,
                            c.id,
                            r.code,
                          )
                            .then(() => reload())
                            .catch((err: unknown) =>
                              setError(
                                err instanceof Error ? err.message : "Failed",
                              ),
                            )
                            .finally(() => setBusy(false));
                        }}
                        className={`inline-flex min-h-9 items-center gap-1 rounded-full px-2.5 text-sm transition-colors ${
                          myReact
                            ? "bg-[color-mix(in_oklab,var(--color-primary)_16%,transparent)]"
                            : "hover:bg-[var(--color-background)]"
                        }`}
                      >
                        <span aria-hidden>{r.label}</span>
                        {count > 0 ? (
                          <span className="text-xs text-[var(--color-muted)]">
                            {count}
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                  {mine && !editing && (
                    <>
                      <button
                        type="button"
                        disabled={busy}
                        className="min-h-9 rounded-full px-2.5 text-xs text-[var(--color-muted)] hover:bg-[var(--color-background)] hover:text-[var(--color-foreground)]"
                        onClick={() => {
                          setEditingId(c.id);
                          setEditBody(c.body);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        className="min-h-9 rounded-full px-2.5 text-xs text-[var(--color-muted)] hover:bg-[var(--color-background)] hover:text-[var(--color-destructive)]"
                        onClick={() => {
                          void (async () => {
                            const ok = await confirm({
                              title: "Delete comment?",
                              description:
                                "This removes your comment from the thread.",
                              confirmLabel: "Delete",
                              destructive: true,
                            });
                            if (!ok) return;
                            setBusy(true);
                            setError(null);
                            try {
                              await deleteComment(companySlug, c.id);
                              await reload();
                            } catch (err: unknown) {
                              setError(
                                err instanceof Error ? err.message : "Failed",
                              );
                            } finally {
                              setBusy(false);
                            }
                          })();
                        }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
        {comments.length === 0 && (
          <li className="rounded-xl bg-[var(--color-background)]/70 px-4 py-6 text-center text-sm text-[var(--color-muted)]">
            No comments yet — @ someone if they should see this.
          </li>
        )}
      </ul>

      <form
        className="mt-5 flex gap-3 border-t border-[var(--color-border)]/70 pt-4"
        onSubmit={(e) => void onSubmit(e)}
      >
        <PersonAvatar
          label={meLabel}
          size="lg"
          variant="accent"
          className="mt-1"
        />
        <div className="min-w-0 flex-1">
          {!composerOpen ? (
            <button
              type="button"
              className="flex min-h-11 w-full items-center rounded-full border border-[var(--color-border)] bg-[var(--color-background)]/80 px-4 text-left text-sm text-[var(--color-muted)] transition-colors hover:border-[var(--color-muted-fg)]"
              onClick={() => {
                setComposerOpen(true);
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
            >
              Write a comment…
            </button>
          ) : (
            <div className="relative space-y-2">
              <Textarea
                ref={textareaRef}
                className="min-h-[5.5rem] rounded-2xl"
                placeholder="Write a comment… type @ for people or docs"
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  setCursor(e.target.selectionStart);
                }}
                onSelect={(e) => {
                  setCursor((e.target as HTMLTextAreaElement).selectionStart);
                }}
                onKeyUp={(e) => {
                  setCursor((e.target as HTMLTextAreaElement).selectionStart);
                }}
              />
              {mention &&
                (peopleSuggestions.length > 0 ||
                  docSuggestions.length > 0 ||
                  docSearchBusy) && (
                <ul
                  className="absolute left-0 right-0 top-full z-10 mt-1 max-h-56 list-none overflow-auto rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-1.5 shadow-[0_16px_40px_color-mix(in_oklab,var(--color-foreground)_12%,transparent)]"
                  role="listbox"
                >
                  {peopleSuggestions.length > 0 ? (
                    <li className="m-0 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                      People
                    </li>
                  ) : null}
                  {peopleSuggestions.map((m) => (
                    <li key={`u-${m.id}`} className="m-0 p-0">
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-xl px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickMention(m.id);
                        }}
                      >
                        <PersonAvatar
                          label={m.label}
                          size="md"
                          variant="solid"
                        />
                        {m.label}
                      </button>
                    </li>
                  ))}
                  {docSuggestions.length > 0 ? (
                    <li className="m-0 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-muted)]">
                      Documents
                    </li>
                  ) : null}
                  {docSuggestions.map((d) => (
                    <li key={`d-${d.kind}-${d.id}`} className="m-0 p-0">
                      <button
                        type="button"
                        className="flex w-full flex-col items-start rounded-xl px-2.5 py-2 text-left text-sm hover:bg-[var(--color-background)]"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          pickDocMention(d.kind, d.id);
                        }}
                      >
                        <span className="font-medium">{d.label}</span>
                        <span className="text-xs text-[var(--color-muted)]">
                          {d.kind.replace("_", " ")}
                          {d.hint ? ` · ${d.hint}` : ""}
                        </span>
                      </button>
                    </li>
                  ))}
                  {docSearchBusy && docSuggestions.length === 0 ? (
                    <li className="m-0 px-2.5 py-2 text-xs text-[var(--color-muted)]">
                      <PendingInline>Searching documents…</PendingInline>
                    </li>
                  ) : null}
                </ul>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  size="sm"
                  disabled={busy || !body.trim()}
                >
                  Post
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={busy}
                  onClick={() => {
                    setComposerOpen(false);
                    setBody("");
                    setMentionIds([]);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </form>
      {error && (
        <Alert variant="destructive" className="mt-3">
          {error}
        </Alert>
      )}
      <DocMentionPreview
        open={preview != null}
        companySlug={companySlug}
        kind={preview?.kind ?? null}
        docId={preview?.id ?? null}
        onOpenChange={(open) => {
          if (!open) setPreview(null);
        }}
      />
    </>
  );

  if (embedded) return <div>{thread}</div>;

  return (
    <section className="mt-10 border-t border-[var(--color-border)] pt-6">
      <h2 className="m-0 mb-4 text-sm font-semibold text-[var(--color-foreground)]">
        Comments
      </h2>
      {thread}
    </section>
  );
}
